import { ICommissionTypeFrontEndModel, IDurationUnit } from "../../types";
import { ILanguageCourseWizardConfigs } from "../wizard/types";

//***************** Interfaces *****************//
export interface ICountry {
  code: string;
  name: string;
}

export interface ILanguageAutocompleteValue {
  id: string;
  name: string;
  popularity: string;
}

export interface ICourseComponentProps {
  fieldsGroupName: string;
  errors?: { [key: string]: string };
}

export interface IRequiredDoc {
  id?: string;
  name: string;
  shortName?: string;
  longName?: string;
}

export interface IBranchBackEndModel {
  branchId: string;
  deactivationNote?: {
    text: string;
  };
}

export interface IGetBranchBackEndModel {
  address: string;
  city: string;
  country: string;
  id: string;
  name: string;
  state: string;
  zip: string;
}

interface IRequiredDocsBackEndModel {
  name: string;
}

interface IRequiredDocsBackEndViewModel {
  id: string;
  name: string;
}

interface IApplicationFormsBackEndModel {
  fileId: string;
}

interface IApplicationFormsBackEndViewModel {
  fileId: string;
  path: string;
  size: number;
  name: string;
}

export interface IApplicationFormsFrontEndModel {
  id: string;
  name: string;
  size: number;
  preview?: string;
}

export interface ICurrencyFrontEndModel {
  id: number;
  name: string;
}

export interface IOtherFeesFrontEndModel {
  name: string;
  amount: string;
}

export interface IOtherFeesBackEndModel {
  name: string;
  amount: number;
}

export interface IDetailsFrontEndModel {
  [key: string]: string;
}

export interface IDetailsViewModel {
  content: string;
  type: number;
  typeName: string;
}

export interface IDetailBackEndModel {
  type: number;
  content: string;
}

export interface IDetailFileFrontEndModel {
  id: string;
  name: string;
  size: number;
  path?: string;
  preview?: string;
}

export interface IDetailFileBackEndModel {
  fileId: string;
}

export interface IDetailFileViewBackEndModel {
  fileId: string;
  path: string;
  size: number;
  name: string;
}

interface IRestrictedCountriesFrontEndModel {
  code: string;
  name: string;
}

interface IBranchesWithAppCount {
  applicationsCount: {
    approved: number;
    pending: number;
  };
  branchId: string;
  deactivationNote?: {
    text: string;
  };
}

export interface IStep1 {
  language: ILanguageAutocompleteValue;
  category: string;
  courseType: string;
  minAge: string;
  maxAge?: string;
  branches: string[];
  branchesWithAppCount?: { [key: string]: IBranchesWithAppCount };
}

export interface IStep2 {
  requiredDocs: IRequiredDoc[];
  additionalDocuments: { [key: string]: string };
  applicationForms?: IApplicationFormsFrontEndModel[];
  applicationLink?: string;
  durationUnit: IDurationUnit;
  durationAmount: string;
  housingTypes?: number[];
  restrictedCountries?: IRestrictedCountriesFrontEndModel[];
}

export interface IStep3 {
  price: string;
  currency: ICurrencyFrontEndModel;
  priceDurationUnit: IDurationUnit;
  priceDurationAmount?: string;
  commissionAmount: string;
  commissionType: ICommissionTypeFrontEndModel;
  residenceAmount?: string;
  homestayAmount?: string;
  insuranceAmount?: string;
  mainOtherFees?: IOtherFeesFrontEndModel[];
  addedOtherFees?: IOtherFeesFrontEndModel[];
  hasPriceDiscount?: boolean;
  hasOtherFeesDiscount?: boolean;
  priceDiscountType?: ICommissionTypeFrontEndModel;
  priceDiscountAmount?: string;
  otherFeesDiscountType?: ICommissionTypeFrontEndModel;
  otherFeesDiscountAmount?: string;
}

export interface IStep4 {
  details: IDetailsFrontEndModel;
  detailFiles?: IDetailFileFrontEndModel[];
}

export interface ICourseFrontEndModel extends IStep1, IStep2, IStep3, IStep4 {
  schoolId: string;
  id?: string;
}

export interface ICourseViewModel {
  language: string;
  category: string;
  courseType: string;
  minAge: string;
  maxAge: string;
  branches: string[];
  requiredDocs: string[];
  applicationForms?: IApplicationFormsFrontEndModel[];
  applicationLink?: string;
  courseDuration: string;
  housingTypes?: number[];
  restrictedCountries?: string[];
  price: string;
  priceDuration: string;
  commissionAmount: string;
  accommodationFees?: IAccommodationFeeViewModel[];
  otherFees: IOtherFeesFrontEndModel[];
  otherFeesSum: string;
  priceDiscount: string;
  calculatedPrice: string;
  otherFeesDiscount: string;
  calculatedOtherFees: string;
  hasPriceSupport: boolean;
  hasOtherFeesSupport: boolean;
  details: IDetailsViewModel[];
  detailFiles: IDetailFileFrontEndModel[];
}

export interface ICourseBackEndModel {
  id?: string;
  schoolId: string;
  language: string;
  category: string;
  courseType: number;
  minAge: number;
  maxAge: number | null;
  branches: IBranchBackEndModel[];
  requiredDocs: IRequiredDocsBackEndModel[];
  applicationForms: IApplicationFormsBackEndModel[];
  applicationLink: string | null;
  durationUnit: number;
  durationAmount: number;
  housingTypes: number[];
  restrictedCountries: string[];
  price: number;
  currency: number;
  priceDurationUnit: number;
  priceDurationAmount: number | null;
  commissionAmount: number;
  commissionType: number;
  residenceAmount: number | null;
  homestayAmount: number | null;
  insuranceAmount: number | null;
  otherFees: IOtherFeesBackEndModel[];
  hasPriceDiscount: boolean;
  hasOtherFeesDiscount: boolean;
  priceDiscountType?: number;
  priceDiscountAmount?: number;
  otherFeesDiscountType?: number;
  otherFeesDiscountAmount?: number;
  details: IDetailBackEndModel[];
  detailFiles: IDetailFileBackEndModel[];
}

export interface ICourseBackEndViewModel {
  id: string;
  language: string;
  category: string;
  courseType: number;
  minAge: number;
  maxAge: number | null;
  branches: IBranchesWithAppCount[];
  requiredDocs: IRequiredDocsBackEndViewModel[];
  applicationForms: IApplicationFormsBackEndViewModel[];
  applicationLink: string | null;
  durationUnit: number;
  durationAmount: number;
  housingTypes: number[];
  restrictedCountries: string[];
  price: number;
  currency: number;
  priceDurationUnit: number;
  priceDurationAmount: number | null;
  commissionAmount: number;
  commissionType: number;
  residenceAmount: number | null;
  homestayAmount: number | null;
  insuranceAmount: number | null;
  otherFees: IOtherFeesBackEndModel[];
  hasPriceDiscount: boolean;
  hasOtherFeesDiscount: boolean;
  priceDiscountType: number | null;
  priceDiscountAmount: number | null;
  otherFeesDiscountType: number | null;
  otherFeesDiscountAmount: number | null;
  details: IDetailBackEndModel[];
  detailFiles: IDetailFileViewBackEndModel[];
}

export interface IAccommodationFeeViewModel {
  label: string;
  amount: string;
}

export interface ILanguageCoursesContext {
  languageCourseWizardConfigs: ILanguageCourseWizardConfigs;
  refetchLanguageCourses: () => Promise<any>;
  submitHandler?: any;
}

export interface ICourseListItemBackEndModel {
  id: string;
  language: string;
  category: string;
  courseType: number;
  durationUnit: number;
  durationAmount: number;
  housingTypes: number[];
  minAge: number;
  maxAge: number | null;
  otherFees: IOtherFeesBackEndModel[];
  isActive: boolean;
  currency: number;
  price: number;
  commissionAmount: number;
  commissionType: number;
  hasPriceDiscount: boolean;
  priceDiscountType: number | null;
  priceDiscountAmount: number | null;
  hasOtherFeesDiscount: boolean;
  otherFeesDiscountType: number | null;
  otherFeesDiscountAmount: number | null;
  residenceAmount: number | null;
  homestayAmount: number | null;
  insuranceAmount: number | null;
}

export interface ICourseFinanceListItemFrontEndModel {
  label: string;
  value: string;
}

export interface ICourseListItemFrontEndModel {
  id: string;
  category: string;
  courseType: string;
  ageRange: string;
  housingTypes: string[];
  isActive: boolean;
  finance: ICourseFinanceListItemFrontEndModel[];
}

export interface ILanguageCourseListItem {
  [key: string]: ICourseListItemBackEndModel[];
}

export interface ILanguageListItem {
  id: string;
  name: string;
  courses: ICourseListItemBackEndModel[];
}

//***************** Enums *****************//
export enum CourseType {
  ONLINE = 1,
  ON_CAMPUS = 2,
}

export enum CourseDetailType {
  COURSE_DESCRIPTION = 1,
  AREAS_OF_CONCENTRATION = 2,
  START_DATES = 3,
  PRICES = 4,
  PAYMENT_PLAN_OPTIONS = 5,
  DISCOUNT = 6,
  OTHER_FEES = 7,
  REQUIRED_DOCUMENTS = 8,
  DEPOSIT_AND_REFUND_POLICY = 9,
  EXAMS = 10,
  LOCATIONS = 11,
  CANCELLATION_POLICY = 12,
  OTHER_COMMENTS = 13,
  FAQ = 14,
}

export enum LanguageCourseAccommodationFee {
  RESIDENCE = "residence",
  HOMESTAY = "homestay",
  INSURANCE = "insurance",
}

export enum CourseHousingType {
  RESIDENCE = 1,
  HOMESTAY = 2,
  NO_HOUSING = 3,
}

export enum CourseExpandedState {
  CLOSED = 0,
  PRICE = 1,
}

//***************** Types *****************//
export type TGetAllLanguagesGroupedByPopularity = (
  popularLanguages: string[],
) => ILanguageAutocompleteValue[];

export type TParseToCheckDuplicationModel = (values: {
  schoolId: string;
  id?: string;
  language: ILanguageAutocompleteValue;
  category: string;
  courseType: string;
  minAge: string;
  maxAge?: string;
  branches: string[];
}) => {
  schoolId: string;
  language: string;
  category: string;
  courseType: number;
  minAge: number;
  maxAge: number | null;
  branches: IBranchBackEndModel[];
};

export type TMergeCourseSteps = (data: {
  step1: IStep1;
  step2: IStep2;
  step3: IStep3;
  step4: IStep4;
  schoolId: string;
  id?: string;
}) => ICourseFrontEndModel;

export type CalcFeeWithDiscount = ({
  fee,
  hasFeeDiscount,
  feeDiscountType,
  feeDiscountAmount,
  currency,
}: {
  fee: number;
  hasFeeDiscount: boolean;
  feeDiscountType: number | null;
  feeDiscountAmount: number | null;
  currency: number;
}) => {
  discountWithCurrency: string;
  calcFeeWithCurrency: string;
  feeWithCurrency: string;
  hasSupport: boolean;
  calcFee: number;
};

export type SumPriceAndOtherFeesSupports = (
  price: number,
  hasPriceSupport: boolean,
  PriceWithSupport: number,
  otherFees: number,
  hasOtherFeesSupport: boolean,
  otherFeesWithSupport: number,
  currencyId: number,
) => {
  priceOtherFeesSumWithCurrency: string;
  priceOtherFeesSum: number;
  priceOtherFeesWithSupportsSumWithCurrency: string;
  priceOtherFeesWithSupportsSum: number;
  hasFeesSupport: boolean;
};
